import { useRef } from "react";

interface Step3Props {
  mesaj: string;
  onChange: (val: string) => void;
}

const DEGISKENLER = [
  { key: "%veli_adi", label: "Veli Adı" },
  { key: "%ogrenci_adi", label: "Öğrenci Adı" },
  { key: "%telefon", label: "Telefon" },
  { key: "%odeme_tarihi", label: "Ödeme Tarihi" },
  { key: "%taahhut_bitis_tarihi", label: "Taahhüt Bitiş" },
  { key: "%kampanya_adi", label: "Kampanya Adı" },
  { key: "%son_tarih", label: "Son Tarih" },
  { key: "%duraklatma_formu", label: "Duraklatma Formu" },
];

const SABLONLAR = [
  {
    label: "Referans İsteme",
    text: "Merhaba %veli_adi, çocuğunuz %ogrenci_adi GoStudent ile büyük gelişim gösterdi. Bu başarıyı çevrenizle paylaşır mısınız? Referans formumuz: %duraklatma_formu",
  },
  {
    label: "Ödeme Hatırlatma",
    text: "Sayın %veli_adi, %ogrenci_adi için bu ayki ödemeniz %odeme_tarihi tarihinde. Herhangi bir sorunuz için lütfen iletişime geçin.",
  },
  {
    label: "Taahhüt Bitiş",
    text: "Sayın %veli_adi, %ogrenci_adi adlı öğrencinizin taahhüt süresi %taahhut_bitis_tarihi tarihinde sona ermektedir. Yenileme hakkında görüşmek ister misiniz?",
  },
];

function WhatsappPreview({ mesaj }: { mesaj: string }) {
  const preview = mesaj
    .replace(/%veli_adi/g, "Ayşe Hanım")
    .replace(/%ogrenci_adi/g, "Zeynep Yılmaz")
    .replace(/%telefon/g, "0532 111 2233")
    .replace(/%odeme_tarihi/g, "5 Haziran 2026")
    .replace(/%taahhut_bitis_tarihi/g, "15 Ağustos 2026")
    .replace(/%kampanya_adi/g, "Haziran Kampanyası")
    .replace(/%son_tarih/g, "30 Haziran 2026")
    .replace(/%duraklatma_formu/g, "https://form.link/xyz");

  return (
    <div className="flex flex-col h-full">
      <div className="bg-[#075E54] rounded-t-2xl px-4 py-3 flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
          <span className="text-white text-xs font-bold">AY</span>
        </div>
        <div>
          <p className="text-white text-sm font-medium">Ayşe Yılmaz</p>
          <p className="text-[#25D366] text-[11px]">Zeynep Yılmaz veli</p>
        </div>
      </div>
      <div
        className="flex-1 rounded-b-2xl p-4 min-h-[260px]"
        style={{ background: "url(https://readdy.ai/api/search-image?query=whatsapp%20chat%20pattern%20background%20very%20light%20mint%20subtle%20squares&width=300&height=300&seq=wabg22&orientation=squarish) #e5ddd5" }}
      >
        <div className="flex justify-start">
          {mesaj.trim() ? (
            <div className="bg-white rounded-tl-none rounded-tr-2xl rounded-br-2xl rounded-bl-2xl p-3 max-w-[90%] shadow-sm">
              <p className="text-[13px] text-gray-800 leading-relaxed whitespace-pre-wrap">{preview}</p>
              <div className="flex items-center justify-between mt-2 gap-2">
                <span className="text-[10px] text-gray-400">14:32</span>
                <span className="text-[10px] text-[#25D366]">✓✓</span>
              </div>
            </div>
          ) : (
            <div className="bg-white/60 rounded-2xl p-4 text-center w-full">
              <p className="text-xs text-gray-400">Mesaj önizlemesi burada görünecek...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Step3Mesaj({ mesaj, onChange }: Step3Props) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const charCount = mesaj.length;
  const lineCount = mesaj.split("\n").length;

  const insertVariable = (variable: string) => {
    const ta = textareaRef.current;
    if (!ta) {
      onChange(mesaj + variable);
      return;
    }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const newVal = mesaj.slice(0, start) + variable + mesaj.slice(end);
    onChange(newVal);
    setTimeout(() => {
      ta.selectionStart = ta.selectionEnd = start + variable.length;
      ta.focus();
    }, 0);
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-bold text-gray-900 mb-1">WhatsApp Mesajı Hazırla</h3>
        <p className="text-sm text-gray-500">Mesajınızı yazın, değişken ekleyin ve önizleyin.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Editor */}
        <div className="space-y-4">
          {/* Template selector */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-2">Şablon Seç</label>
            <div className="flex gap-2 flex-wrap">
              {SABLONLAR.map((s) => (
                <button
                  key={s.label}
                  onClick={() => onChange(s.text)}
                  className="px-3 py-1.5 text-xs border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 hover:border-gray-300 cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-file-text-line mr-1"></i>{s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Textarea */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-2">Mesaj Metni *</label>
            <textarea
              ref={textareaRef}
              value={mesaj}
              onChange={(e) => onChange(e.target.value)}
              placeholder="WhatsApp mesajınızı buraya yazın. Kişiselleştirmek için aşağıdaki değişkenleri ekleyin..."
              className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#25D366]/30 focus:border-[#25D366] bg-white resize-none h-44 leading-relaxed"
              maxLength={4096}
            />
            <div className="flex items-center justify-between mt-1.5 text-xs text-gray-400">
              <span>{lineCount} satır</span>
              <span>{charCount} / 4096 karakter</span>
            </div>
          </div>

          {/* Variable buttons */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-2">Değişken Ekle</label>
            <div className="flex flex-wrap gap-1.5">
              {DEGISKENLER.map((d) => (
                <button
                  key={d.key}
                  onClick={() => insertVariable(d.key)}
                  className="px-2.5 py-1 text-xs bg-[#0B1120]/5 text-[#0B1120] border border-[#0B1120]/10 rounded-lg hover:bg-[#0B1120]/10 cursor-pointer whitespace-nowrap transition-colors font-mono"
                >
                  {d.key}
                </button>
              ))}
            </div>
          </div>

          {/* Helper */}
          <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-xl border border-amber-100">
            <div className="w-4 h-4 flex items-center justify-center text-amber-500 mt-0.5 flex-shrink-0">
              <i className="ri-information-line text-sm"></i>
            </div>
            <p className="text-xs text-amber-700 leading-relaxed">
              Mesajınız gönderim sırasında kişiye özel bilgilerle otomatik olarak düzenlenecektir.
            </p>
          </div>
        </div>

        {/* Right: WhatsApp Preview */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-2">WhatsApp Önizleme</label>
          <div className="max-w-[300px] mx-auto">
            {/* Phone frame */}
            <div className="relative">
              <div className="absolute inset-x-0 -top-2 flex justify-center">
                <div className="w-20 h-1.5 bg-gray-800 rounded-full"></div>
              </div>
              <div className="border-4 border-gray-800 rounded-3xl overflow-hidden">
                <WhatsappPreview mesaj={mesaj} />
              </div>
              <div className="absolute inset-x-0 -bottom-3 flex justify-center">
                <div className="w-12 h-1.5 bg-gray-800 rounded-full"></div>
              </div>
            </div>
            <p className="text-center text-xs text-gray-400 mt-5">Veli: Ayşe Yılmaz · Öğrenci: Zeynep Yılmaz</p>
          </div>
        </div>
      </div>
    </div>
  );
}